import { cacheKeys } from "@constants/cache-keys";
import { ACTIVE_ACCESS_CODES_STALE_TIME } from "@constants/stale-times";
import { events } from "@constants/tracking";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { switchExamContext } from "@features/redeem/helpers";
import {
	fetchActivePurchases,
	getRedeemedExamId,
	getRedeemedExamName,
	shouldPromptAutoRedeem,
} from "@features/redeem/helpers/auto-redeem";
import { useGlobalStore } from "@hooks/useGlobalStore";
import type { RedeemStatus } from "@memorang/ui/components/AutoRedeem/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
import { applyAccessCode } from "api/commerce/access-code-service";
import { refreshExamsListCache } from "api/exams-query";
import { usePathname } from "expo-router";
import { useState } from "react";

const AUTO_REDEEM_SOURCE = "auto_redeem_dialog";

export const useAutoRedeemDialog = () => {
	const { isSignedIn, userLogicalId, email } = useAuthContext();
	const { isExamContextLoading, handleSwitchExamContext } = useExamContext();
	const isAuthFlowExamContextSwitching = useGlobalStore(
		(state) => state.isAuthFlowExamContextSwitching,
	);
	const setAutoRedeemLastPromptAt = useGlobalStore(
		(state) => state.setAutoRedeemLastPromptAt,
	);
	const autoRedeemLastPromptAt = useGlobalStore(
		(state) => state.autoRedeemLastPromptAt,
	);
	const promptRequestId = useGlobalStore(
		(state) => state.autoRedeemPromptRequestId,
	);
	const pathname = usePathname();
	const queryClient = useQueryClient();

	const [dismissed, setDismissed] = useState(false);
	const [deselectedIds, setDeselectedIds] = useState<string[]>([]);
	const [redeemStatus, setRedeemStatus] = useState<RedeemStatus | undefined>();
	const [redeemedProductIds, setRedeemedProductIds] = useState<string[]>([]);
	const [redeemedProductsExamName, setRedeemedProductsExamName] = useState("");
	const [seenPromptRequestId, setSeenPromptRequestId] =
		useState(promptRequestId);

	if (promptRequestId !== seenPromptRequestId) {
		setSeenPromptRequestId(promptRequestId);
		if (promptRequestId > seenPromptRequestId) {
			setDismissed(false);
			setDeselectedIds([]);
			setRedeemedProductIds([]);
			setRedeemStatus(undefined);
		}
	}

	const isHomeRoute = pathname === "/home";

	const enabled =
		!!isSignedIn &&
		!!userLogicalId &&
		!isExamContextLoading &&
		!isAuthFlowExamContextSwitching &&
		isHomeRoute &&
		shouldPromptAutoRedeem(autoRedeemLastPromptAt[userLogicalId]);

	const { data: activePurchases } = useQuery({
		queryKey: [cacheKeys.activeAccessCodes, userLogicalId],
		queryFn: fetchActivePurchases,
		enabled,
		staleTime: ACTIVE_ACCESS_CODES_STALE_TIME,
		retry: false,
		refetchOnWindowFocus: false,
	});

	const allPurchases = activePurchases ?? [];
	const purchases =
		redeemStatus === "redeemed"
			? allPurchases.filter((purchase) =>
					redeemedProductIds.includes(purchase.id),
				)
			: allPurchases;
	const selectedProducts =
		redeemStatus === "redeemed"
			? redeemedProductIds
			: allPurchases
					.map((purchase) => purchase.id)
					.filter((id) => !deselectedIds.includes(id));
	const visible = allPurchases.length > 0 && !dismissed;

	const handleSelectProduct = (id: string) => {
		setDeselectedIds((prev) =>
			prev.includes(id)
				? prev.filter((productId) => productId !== id)
				: [...prev, id],
		);
	};

	const handleDismiss = () => {
		setDismissed(true);
		if (userLogicalId) {
			setAutoRedeemLastPromptAt(userLogicalId);
		}
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
	const handleAction = async () => {
		if (redeemStatus === "redeemed") {
			handleDismiss();
			return;
		}

		setRedeemStatus("redeeming");

		const selectedPurchases = purchases.filter((purchase) =>
			selectedProducts.includes(purchase.id),
		);

		let redeemedExamId: string | undefined;
		let redeemedExamName: string | undefined;
		const redeemedIds: string[] = [];

		for (const purchase of selectedPurchases) {
			const accessCode = purchase.codes?.[0];
			if (!accessCode) {
				continue;
			}

			trackCustomEvent({
				eventName: events.promoCodeEntered,
				source: AUTO_REDEEM_SOURCE,
				examId: accessCode,
			});

			try {
				const result = await applyAccessCode({ accessCode });
				if (!result.success) {
					continue;
				}

				trackCustomEvent({
					eventName: events.promoCodeRedeemed,
					source: AUTO_REDEEM_SOURCE,
					examId: getRedeemedExamId(result),
					examNumericId: result.data.examNumericId,
					code: accessCode,
				});

				redeemedExamId = getRedeemedExamId(result) ?? redeemedExamId;
				redeemedExamName = getRedeemedExamName(result) ?? redeemedExamName;
				redeemedIds.push(purchase.id);
			} catch {
				// keep redeeming the remaining purchases
			}
		}

		if (!redeemedExamId) {
			setRedeemStatus(undefined);
			handleDismiss();
			return;
		}

		await refreshExamsListCache(queryClient);
		queryClient.invalidateQueries({
			queryKey: [cacheKeys.activeAccessCodes],
			refetchType: "none",
		});
		await switchExamContext(redeemedExamId, handleSwitchExamContext);
		setRedeemedProductIds(redeemedIds);
		setRedeemedProductsExamName(redeemedExamName ?? "");
		setRedeemStatus("redeemed");
	};

	return {
		visible,
		purchases,
		email: email ?? "",
		redeemStatus,
		selectedProducts,
		redeemedProductsExamName,
		handleSelectProduct,
		handleDismiss,
		handleAction,
	};
};
